// Bibliotecas externas
import React, { useState, useEffect, useCallback } from 'react'; // React e hooks para gerenciamento de estado
import {
  Typography, // Componente para exibição de texto com estilo
  Box, // Componente de container flexível
  CircularProgress, // Indicador de carregamento circular
  Grid, // Grid tradicional do Material-UI
  Alert // Componente de alerta
} from '@mui/material'; // Biblioteca de componentes Material UI
import { ThemeProvider } from '@mui/material/styles'; // Provedor de tema para Material UI

// Componentes
// -- Layout
import PageContainer from '../components/Layout/PageContainer'; // Container principal da página
import Sidebar from '../components/Sidebar'; // Barra lateral de navegação
import Header from '../components/Header'; // Cabeçalho da página
import WelcomeHeader from '../components/WelcomeHeader';

// -- Dashboard
import ResumoCard from '../components/Dashboard/ResumoCard'; // Card para exibição de estatísticas resumidas
import DashboardFilters from '../components/Dashboard/DashboardFilters'; // Filtros do dashboard

// -- Gráficos
import CorretorBarChart from '../components/Charts/CorretorBarChart'; // Gráfico de barras para corretores

// -- Funcionalidades 
import ModoEspiao from '../components/ModoEspiao'; // Componente para monitoramento de atividades
import TabsContainer from '../components/Tabs/TabsContainer'; // Container para abas de navegação
import AssistenteVendasGestao from '../components/Tabs/AssistenteVendasGestao/AssistenteVendasGestao'

// Hooks
import useDashboardData from '../hooks/useDashboardData'; // Hook personalizado para dados do dashboard

// Utilitários
import { truncate } from '../utils/text'; // Função para truncar texto
import axios from 'axios'; // Cliente HTTP para requisições
import { jwtDecode } from 'jwt-decode'; // Decodificador de JWT

// Estilos/Temas
import theme from '../components/theme/theme'; // Tema personalizado da aplicação

// Token Validation Components
import { useTokenValidation, TokenExpiredDialog } from '../utils/tokenValidation';

/**
 * AssistenteVendas - Componente principal do Agente do Corretor
 * 
 * Este componente gerencia a exibição de dados relacionados a vendas,
 * incluindo dashboard, gestão e modo espião através de abas.
 */
const AssistenteVendas = () => {
  // ========== VALIDAÇÃO DE TOKEN ==========
  const { showExpiredDialog, countdown, redirectToHome } = useTokenValidation();

  // Estado para controlar qual aba está selecionada (0: Dashboard, 1: Gestão, 2: Modo Espião)
  const [tabIndex, setTabIndex] = useState(0);

  // Estados para corretores e gestores
  const [, setListaCorretoresGestores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para o Modo Espião
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState(null);

  const [forceTokenExpired, setForceTokenExpired] = useState(false);

  // URL base da API, obtida das variáveis de ambiente
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Manipulador para mudança de abas
  const handleTabChange = useCallback((_, newIndex) => {
    setTabIndex(newIndex);
    setError(null); // Limpa erros ao trocar de aba
  }, []);

  // Estado para filtro de data no dashboard, inicializado com "Mês Atual"
  const [dateFilter, setDateFilter] = useState('Mês atual');

  // Hook para carregar dados do dashboard com base no filtro de data
  const {
    loading: loadingDashboard, // Estado de carregamento
    resumo, // Dados resumidos de estatísticas
    chartData, // Dados para o gráfico
    sortOption, // Opção de ordenação atual
    setSortOption // Função para alterar a ordenação
  } = useDashboardData(dateFilter, baseURL, setForceTokenExpired);

  // Processamento dos dados do gráfico
  const corretoresGrafico = chartData.map(item => truncate(item.nome, 27)); // Nomes dos corretores truncados para 27 caracteres
  const mensagensEnviadas = chartData.map(item => item.totalMensagens); // Total de mensagens enviadas por corretor

  /**
   * Hook de efeito para buscar dados dos corretores e gestores
   * Executa requisição à API quando o componente é montado
   */
  useEffect(() => {
    /**
     * Função assíncrona para buscar dados dos corretores e gestores
     * Realiza requisição à API: /api/produto/AV/lista-corretores-gestores
     */
    const fetchCorretoresGestores = async () => {
      // Recupera token de autenticação do localStorage
      const token = localStorage.getItem('jwtToken');

      if (!token) {
        console.error('❌ [AUTH] Token não encontrado no localStorage');
        setError('Token de autenticação não encontrado');
        return;
      }

      // Indica início do carregamento
      setLoading(true);
      setError(null);

      try {
        // Decodifica token para obter ID do cliente
        const decoded = jwtDecode(token);
        const idCliente = decoded.id_cliente;

        // Log da URL que será chamada
        const apiUrl = `${baseURL}/produto/AV/lista-corretores-gestores`;
        console.log('🔄 [API] Buscando corretores e gestores:', apiUrl);

        // Requisição para buscar lista de corretores e gestores
        const response = await axios.get(apiUrl, {
          params: { idCliente },
          headers: { Authorization: `Bearer ${token}` }
        });

        console.log('✅ [API] Dados recebidos:', response.data);

        // Atualiza estado com dados recebidos
        // Assume que a API retorna um array de objetos com as propriedades: nome, telefone, papel
        setListaCorretoresGestores(response.data || []);

      } catch (error) {
        if (
          error.response?.status === 401 ||
          error.response?.data?.message?.toLowerCase().includes("token") // ou "expired"
        ) {
          setForceTokenExpired(true); // Força abrir o diálogo
        }
        console.error('❌ [API] Erro ao buscar corretores e gestores:', error);
      } finally {
        // Finaliza estado de carregamento
        setLoading(false);
      }
    };

    // Chama função de busca apenas se baseURL estiver definida
    if (baseURL) {
      fetchCorretoresGestores();
    }
  }, [baseURL]); // Dependência que trigga nova busca

  useEffect(() => {
    if (tabIndex === 2) {
      setLoadingContacts(true);
      setContactsError(null);

      const fetchContacts = async () => {
        try {
          const token = localStorage.getItem('jwtToken');

          if (!token) {
            throw new Error('Token de autenticação não encontrado');
          }

          const decodedToken = jwtDecode(token);
          const idCliente = decodedToken.id_cliente;

          if (!idCliente) {
            throw new Error('ID do cliente não encontrado no token');
          }

          const response = await axios.get(`${baseURL}/produto/AV/lista-contatos`, {
            params: { idCliente },
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000 // Timeout de 10 segundos
          });

          if (response.data) {
            setContacts(Array.isArray(response.data) ? response.data : []);
          } else {
            setContacts([]);
          }
        } catch (error) {
          if (
            error.response?.status === 401 ||
            error.response?.data?.message?.toLowerCase().includes("token") // ou "expired"
          ) {
            setForceTokenExpired(true); // Força abrir o diálogo
          }

          console.error('Erro ao buscar contatos:', error);
          setContactsError('Erro ao carregar contatos. Tente novamente.');
          setContacts([]);
        } finally {
          setLoadingContacts(false);
        }
      };

      fetchContacts();
    }
  }, [tabIndex, baseURL]);

  /**
   * Manipula a seleção de contato no Modo Espião
   * @param {Object} contact - Contato selecionado
   */
  const handleContactSelect = useCallback((contact) => {
    setSelectedContact(contact);
    console.log('📱 [MODO ESPIÃO] Contato selecionado:', contact);
  }, []);

  const handleWhatsAppIdClick = useCallback((contact) => {
    if (contact) {
      // Sanitiza o telefone removendo tudo que não é número
      const sanitizedWhatsAppId = contact.telefone.replace(/\D/g, '');

      setSelectedContact({
        ...contact,
        whatsAppId: sanitizedWhatsAppId, // Define corretamente
      });
      setTabIndex(2);
    }
  }, []);


  // Renderização de erro global
  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    // Aplicação do tema personalizado a toda a interface
    <ThemeProvider theme={theme}>
      {/* Container principal com flexbox responsivo */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Coluna em mobile, linha em desktop
          minHeight: '100vh',
          width: '100vw',
          maxWidth: '100%',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'background.default'
        }}
      >
        {/* Barra lateral de navegação responsiva */}
        <Sidebar
          sx={{
            width: { xs: '100%', md: 280 },
            flexShrink: 0,
            order: { xs: 2, md: 1 }, // Em mobile, sidebar fica embaixo
          }}
        />

        {/* Conteúdo principal */}
        <Box
          sx={{
            flexGrow: 1,
            order: { xs: 1, md: 2 }, // Em mobile, conteúdo fica em cima
            width: { xs: '100%', md: 'auto' },
          }}
        >
          {/* Cabeçalho com título da página responsivo */}
          <Header
            title="Corretores"
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
            }}
          />

          {/* Container principal do conteúdo responsivo */}
          <PageContainer
            maxWidth={false}
            sx={{
              width: '100%',
              maxWidth: '100%',
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 1.25, md: 1.5 },
              mb: { xs: 2, md: 0 }, // Margin bottom para mobile
            }}
          >
            {/* Header de boas-vindas moderno */}
            <WelcomeHeader
              title='Corretores'
              subtitle=''
            />

            {/* Abas de navegacao */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: { xs: 2, md: 3 },
                border: '1px solid',
                borderColor: 'divider',
                px: { xs: 1, md: 1.2 },
                mb: { xs: 2, md: 2.4 },
              }}
            >
              <TabsContainer
                tabIndex={tabIndex}
                onTabChange={handleTabChange}
                tabLabels={['Dashboard', 'Gestão', 'Modo Espião']}
                marginB={0}
              />
            </Box>

            {/* Container principal com fundo branco */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: { xs: 2, md: 3 },
                boxShadow: {
                  xs: '0 2px 10px rgba(0,0,0,0.08)',
                  md: '0 4px 20px rgba(0,0,0,0.08)'
                },
                border: '1px solid',
                borderColor: 'divider',
                mb: { xs: 2, md: 0 },
                overflow: 'hidden',
              }}
            >
              {/* Conteúdo das abas com padding responsivo */}
              <Box sx={{ p: { xs: 2, sm: 3, md: 3 }, pb: { xs: 6, sm: 3, md: 3 } }}>

                {/* Conteúdo da aba Dashboard */}
                {tabIndex === 0 && (
                  <Box>
                    {/* Filtros do dashboard com design responsivo */}
                    <DashboardFilters dateFilter={dateFilter} onChange={setDateFilter} hideSpecificDates hideAnual />

                    {/* Exibe indicador de carregamento ou conteúdo baseado no estado de loading */}
                    {loadingDashboard ? (
                      // Indicador de carregamento centralizado
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: { xs: 200, md: 300 },
                          bgcolor: 'rgba(102, 126, 234, 0.05)',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <CircularProgress size={60} sx={{ color: '#667eea' }} />
                      </Box>
                    ) : (
                      // Grid com cards de resumo e gráfico responsivo
                      <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
                        {/* Cards de resumo estatístico */}
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <ResumoCard valor={resumo.gestores} titulo="Resumos enviados para gestores" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <ResumoCard valor={resumo.corretores} titulo="Resumos enviados para corretores" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <ResumoCard valor={resumo.novoLead} titulo="Mensagem de aviso de novo lead" />
                        </Grid>

                        {corretoresGrafico.length > 0 && mensagensEnviadas.length > 0 ? (
                          <Grid size={12}>
                            <CorretorBarChart
                              corretores={corretoresGrafico}
                              mensagens={mensagensEnviadas}
                              sortOption={sortOption}
                              onSortChange={setSortOption}
                              setForceTokenExpired={setForceTokenExpired}
                            />
                          </Grid>
                        ) : (
                          <Grid size={12}>
                            <Typography align="center" sx={{ py: 5 }}>
                              Nenhum dado disponível para exibir o gráfico.
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Box>
                )}

                {/* Conteúdo da aba Gestão */}
                {tabIndex === 1 && (
                  <Box>
                    {/* Estado de carregamento com design moderno */}
                    {loading ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: 400,
                          color: theme.palette.text.secondary,
                          py: 6,
                        }}
                      >
                        <CircularProgress
                          size={60}
                          sx={{
                            mb: 3,
                            color: theme.palette.primary.main
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1,
                            fontWeight: 500,
                            color: theme.palette.primary.main
                          }}
                        >
                          Carregando dados...
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.7,
                            textAlign: 'center',
                            maxWidth: 300
                          }}
                        >
                          Aguarde enquanto buscamos as informações
                        </Typography>
                      </Box>
                    ) : (
                      <AssistenteVendasGestao onContactClick={handleWhatsAppIdClick} />
                    )}
                  </Box>
                )}

                {/* Conteúdo da aba Modo Espião */}
                {tabIndex === 2 && (
                  <Box>
                    <Box
                      sx={{
                        bgcolor: 'rgba(102, 126, 234, 0.05)',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                      }}
                    >
                      {contactsError ? (
                        <Box sx={{ p: 3 }}>
                          <Alert severity="error">
                            {contactsError}
                          </Alert>
                        </Box>
                      ) : (
                        <ModoEspiao
                          contacts={contacts}
                          selectedContact={selectedContact}
                          onSelectContact={handleContactSelect}
                          currentTab="outras-abas"
                          endpointPrefix="AV"
                          loadingContacts={loadingContacts}
                          setForceTokenExpired={setForceTokenExpired}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </PageContainer>
        </Box>
      </Box>
      {/* ========== DIALOG DE TOKEN EXPIRADO - GLOBAL ========== */}
      {/* 
        POSICIONADO AQUI para aparecer independente da aba ativa
        Este dialog ficará sempre no nível mais alto do componente
      */}
      <TokenExpiredDialog
        open={showExpiredDialog || forceTokenExpired}
        countdown={countdown}
        onRedirect={redirectToHome}
      />
    </ThemeProvider>
  );
};

export default AssistenteVendas;
